import { getSortAscending } from "./../../verificationRequest/utils/getSort";
import { TokenData } from "../../../auth/types";
import { TPagingResponse } from "../../../models/PagingResponse";
import { request } from "../../../network/mainClient/request";
import { TApp } from "../../app/models";
import {
  IGeneratedKeys,
  IS3UploadRequest,
  TCompany,
  TOfficer,
} from "../models";
import { CompanyRole } from "../../../auth/AuthProvider";

export const getCompaniesList = async (): Promise<
  TPagingResponse<TCompany>
> => {
  const response = await request({
    path: "/api/v1/company-service",
    method: "GET",
  });

  if (response.ok) {
    return {
      items: response.data.companies,
    };
  }

  throw new Error(response.errorMessage);
};

export const getS3UploadUrl = async (
  fileType: string,
  fileSize: number,
  companyId: string
): Promise<IS3UploadRequest> => {
  const response = await request({
    path: "/api/v1/company-service/upload-url",
    method: "POST",
    body: {
      fileSize,
      fileType,
      companyId,
    },
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const uploadToS3 = async (
  url: string,
  blob: Blob,
  contentType: string
) => {
  const response = await request({
    withoutBaseUrl: true,
    path: url,
    method: "PUT",
    body: blob,
    withAuth: false,
    contentType: contentType,
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const authToCompany = async (id: string): Promise<TokenData> => {
  const response = await request({
    path: "/api/v1/company-service/auth",
    method: "POST",
    body: {
      id,
    },
  });

  if (response.ok && response.data?.token) {
    return response.data.token;
  }

  throw new Error(response.errorMessage);
};

export const getCompany = async (id: string): Promise<TCompany> => {
  const response = await request({
    path: `/api/v1/company-service/${encodeURIComponent(id)}`,
    method: "GET",
  });

  if (response.ok && response.data) {
    return response.data.company;
  }

  throw new Error(response.errorMessage);
};

export enum FourEyesMode {
  STRICT = "FOUR_EYES_MODE_STRICT",
  OPTIONAL = "FOUR_EYES_MODE_OPTIONAL",
  DISABLED = "FOUR_EYES_MODE_DISABLED",
}

export const FourEyesModeView = {
  [FourEyesMode.DISABLED]: "Disabled",
  [FourEyesMode.STRICT]: "Strict",
  [FourEyesMode.OPTIONAL]: "Optional",
};

export type TCreateCompanyData = {
  name: string;
  purpose: string;
  isActive: boolean;
  picture?: string;
  complianceEmail?: string;
  fourEyesMode?: FourEyesMode;
  id?: string;
};

export const createCompany = async (
  data: TCreateCompanyData
): Promise<TCompany> => {
  const response = await request({
    path: "/api/v1/company-service/create",
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.company;
  }

  throw new Error(response.errorMessage);
};

export const updateCompany = async (
  data: TCreateCompanyData
): Promise<TCompany> => {
  const response = await request({
    path: "/api/v1/company-service/update",
    method: "PATCH",
    body: data,
  });

  if (response.ok) {
    return response.data.company;
  }

  throw new Error(response.errorMessage);
};

export const deleteCompany = async (company_id: string): Promise<boolean> => {
  const response = await request({
    path: `/api/v1/company-service/${encodeURIComponent(company_id)}`,
    method: "DELETE",
  });

  if (response.ok) {
    return true;
  }

  throw new Error(response.errorMessage);
};

export type TGenerateKeys = {
  webhookUrl: string;
  applicationName: string;
  companyId: string;
};

export const generateKeys = async (
  data: TGenerateKeys
): Promise<IGeneratedKeys> => {
  const response = await request({
    path: "/api/v1/user-service/generate-keys",
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.keys;
  }

  throw new Error(response.errorMessage);
};

export const getApplicationsList = async (): Promise<TApp[]> => {
  const response = await request({
    path: `/api/v1/user-service/app/list`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.apps;
  }

  throw new Error(response.errorMessage);
};

export const getApplicationsListNew = async (
  companyId: string
): Promise<TApp[]> => {
  const response = await request({
    path: `/api/v1/user-service/${encodeURIComponent(companyId)}/app/list`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.apps;
  }

  throw new Error(response.errorMessage);
};

export type TCompanyRole = {
  companyId: string;
  role: CompanyRole;
  emailNotificationEnabled: boolean;
};

export type TCompanyFormRole = {
  company_id: string;
  role: CompanyRole;
  email_notification_enabled: boolean;
};

export type TCreateOfficerData = {
  phone: string;
  email: string;
  name: string;
  company_role: TCompanyFormRole[];
};

export const changeUserIdToId = (user: any) => {
  return {
    ...user,
    id: user.userId,
  };
};

export const getOfficersList = async (): Promise<TPagingResponse<TOfficer>> => {
  const response = await request({
    path: `/api/v1/user-service/company/user`,
    method: "GET",
  });

  if (response.ok) {
    return {
      items: getSortAscending(
        response.data.companyUser.map((user: any) => changeUserIdToId(user)),
        "email"
      ),
    };
  }

  throw new Error(response.errorMessage);
};

export const createOfficer = async (
  data: TCreateOfficerData
): Promise<TOfficer> => {
  const response = await request({
    path: `/api/v1/user-service/company/user`,
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.companyUser;
  }

  throw new Error(response.errorMessage);
};

export const updateOfficer = async (
  userId: string,
  data: TCreateOfficerData
): Promise<TOfficer> => {
  const response = await request({
    path: `/api/v1/user-service/company/user/${encodeURIComponent(userId)}`,
    method: "PATCH",
    body: data,
  });

  if (response.ok) {
    return response.data.companyUser;
  }

  throw new Error(response.errorMessage);
};

export const changeNotificationEnabled = async (
  companyId: string,
  userId: string
): Promise<TOfficer> => {
  const response = await request({
    path: `/api/v1/user-service/company/${companyId}/user/${userId}/notifications/settings`,
    method: "PATCH",
  });

  if (response.ok) {
    return response.data.companyUser;
  }

  throw new Error(response.errorMessage);
};

export const checkOfficerPhone = async (phone: string): Promise<TOfficer> => {
  const response = await request({
    path: `/api/v1/user-service/company/user/phone/${encodeURIComponent(
      phone
    )}`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.companyUser;
  }

  throw new Error(response.errorMessage);
};

export const checkOfficerEmail = async (email: string): Promise<TOfficer> => {
  const response = await request({
    path: `/api/v1/user-service/company/user/email/${encodeURIComponent(
      email
    )}`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.companyUser;
  }

  throw new Error(response.errorMessage);
};

export const resetUserPassword = async (userId: string): Promise<void> => {
  const response = await request({
    path: `/api/v1/user-service/user/${encodeURIComponent(
      userId
    )}/reset-password`,
    method: "POST",
  });

  if (response.ok) {
    return;
  }

  throw new Error(response.errorMessage);
};
